<script setup lang="ts">
defineProps<{
    items: Array<{
        id: string,
        title: string,
        subtitle?: string,
        at?: unknown,
        icon: any,
        accentClass?: string,
    }>,
    emptyText?: string,
    limit?: number,
}>();

function toMillis(value?: unknown): number {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? 0 : parsed;
    }
    if (typeof value === 'object' && value !== null) {
        const maybeTimestamp = value as { seconds?: number, toDate?: () => Date };
        if (typeof maybeTimestamp.toDate === 'function') return maybeTimestamp.toDate().getTime();
        if (typeof maybeTimestamp.seconds === 'number') return maybeTimestamp.seconds * 1000;
    }
    return 0;
}

function formatRelativeTime(dateValue?: unknown, fallback = 'recently'): string {
    const timestamp = toMillis(dateValue);
    if (!timestamp) return fallback;

    const diff = Date.now() - timestamp;
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.max(1, Math.floor(diff / 60_000))}m ago`;
    if (diff < 86_400_000) return `${Math.max(1, Math.floor(diff / 3_600_000))}h ago`;
    if (diff < 604_800_000) return `${Math.max(1, Math.floor(diff / 86_400_000))}d ago`;
    return `${Math.max(1, Math.floor(diff / 604_800_000))}w ago`;
}
</script>

<template>
    <EmptyState
        v-if="items.length === 0"
        :title="emptyText || 'No recent activity yet.'"
        description=""
        class="min-h-24 py-6"
    />

    <div v-else class="space-y-3">
        <div
            v-for="activity in (typeof limit === 'number' ? items.slice(0, limit) : items)"
            :key="activity.id"
            class="flex items-start gap-3 rounded-2xl px-1 py-1.5"
        >
            <div class="relative flex shrink-0 flex-col items-center">
                <span class="size-2.5 rounded-full bg-violet-500/65 dark:bg-violet-300/80" />
                <span class="mt-1 h-10 w-px bg-border/55" />
            </div>
            <div class="flex min-w-0 flex-1 items-start gap-3">
                <div class="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl border border-border/50 bg-background/72">
                    <component :is="activity.icon" class="size-4" :class="activity.accentClass || 'text-violet-600/90 dark:text-violet-300/90'" />
                </div>
                <div class="min-w-0 flex-1">
                    <p class="text-sm font-medium text-foreground">{{ activity.title }}</p>
                    <p v-if="activity.subtitle" class="mt-1 text-xs text-muted-foreground">{{ activity.subtitle }}</p>
                    <p class="mt-1 text-xs text-muted-foreground">{{ formatRelativeTime(activity.at, 'recently') }}</p>
                </div>
            </div>
        </div>
    </div>
</template>
